import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { Button, Grid } from '@mui/material';
import { HandshakeIcon, HomeIcon, SettingsIcon } from 'lucide-react';
import { PersonPinCircle, QuizRounded, GroupAdd } from '@mui/icons-material';
import GavelIcon from '@mui/icons-material/Gavel';
import PolicyIcon from '@mui/icons-material/Policy';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from '../logo.svg';

function Copyright() {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
      {'Copyright © '}
      <Link color="text.secondary" href="https://falconxcellence.com/">
        Falcon Excellence
      </Link>
      &nbsp;
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function Footer({
  scrollToSection,
  highlightsRef,
  servicesRef,
  whyChooseUsRef,
  faqsRef,
}) {
  const navigate = useNavigate();

  // Define button configurations
  const footerButtons = [
    {
      label: 'Highlights',
      icon: <HomeIcon />,
      onClick: () => scrollToSection(highlightsRef),
    },
    {
      label: 'Services',
      icon: <SettingsIcon />,
      onClick: () => scrollToSection(servicesRef),
    },
    {
      label: 'Why Us',
      icon: <HandshakeIcon />,
      onClick: () => scrollToSection(whyChooseUsRef),
    },
    {
      label: 'FAQ',
      icon: <QuizRounded />,
      onClick: () => scrollToSection(faqsRef),
    },
    {
      label: 'About Us',
      icon: <PersonPinCircle />,
      onClick: () => navigate('/about'),
    },
    {
      label: 'Careers',
      icon: <GroupAdd />,
      onClick: () => navigate('/careers'),
    },
    {
      label: 'Contact Us',
      icon: <ContactMailIcon />,
      onClick: () => (window.location.href = 'mailto:info@falconxcellence.com'),
    },
    {
      label: 'Terms',
      icon: <GavelIcon />,
      onClick: () => navigate('/terms'),
    },
    {
      label: 'Privacy',
      icon: <PolicyIcon />,
      onClick: () => navigate('/privacy'),
    },
  ];

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 4, sm: 8 },
        py: { xs: 8, sm: 10 },
        textAlign: { sm: 'center', md: 'left' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: { xs: '100%', sm: '60%' },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '60%' },
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {/* Logo Image */}
            <img
              src={Logo}
              alt="Logo"
              style={{ height: 50, width: 50, objectFit: 'contain', borderRadius: 8 }}
            />

            {/* Text Section */}
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, mt: 0 }}>
                Let's talk about it.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We are here to answer your questions and discuss solutions.
              </Typography>
            </Box>
          </Box>

          {/* Contact Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailRoundedIcon />
            <Link href="mailto:info@falconxcellence.com" underline="none">
              <Typography variant="body1">info@falconxcellence.com</Typography>
            </Link>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocationOnRoundedIcon />
            <Typography variant="body1">Fairfield, CT, 06824</Typography>
          </Box>
        </Box>

        {/* Desktop Layout (Hidden on Mobile) */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'row',
            gap: 4,
          }}
        >
          {/* Column 1 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {footerButtons.slice(0, 3).map((button, index) => (
              <Button
                key={index}
                variant="text"
                color="info"
                size="small"
                startIcon={button.icon}
                onClick={button.onClick}
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
              >
                {button.label}
              </Button>
            ))}
          </Box>

          {/* Column 2 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {footerButtons.slice(3, 6).map((button, index) => (
              <Button
                key={index}
                variant="text"
                color="info"
                size="small"
                startIcon={button.icon}
                onClick={button.onClick}
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
              >
                {button.label}
              </Button>
            ))}
          </Box>

          {/* Column 3 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {footerButtons.slice(6).map((button, index) => (
              <Button
                key={index}
                variant="text"
                color="info"
                size="small"
                startIcon={button.icon}
                onClick={button.onClick}
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
              >
                {button.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Mobile Layout (Hidden on Desktop) */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            width: '100%',
            gap: 2,
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: 'flex-start' }} // Left-align the grid
          >
            {/* Row 1 */}
            {footerButtons.slice(0, 3).map((button, index) => (
              <Grid item xs={4} key={index}>
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  fullWidth
                  startIcon={button.icon}
                  onClick={button.onClick}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
                >
                  {button.label}
                </Button>
              </Grid>
            ))}

            {/* Row 2 */}
            {footerButtons.slice(3, 6).map((button, index) => (
              <Grid item xs={4} key={index}>
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  fullWidth
                  startIcon={button.icon}
                  onClick={button.onClick}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
                >
                  {button.label}
                </Button>
              </Grid>
            ))}

            {/* Row 3 */}
            {footerButtons.slice(6).map((button, index) => (
              <Grid item xs={4} key={index}>
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  fullWidth
                  startIcon={button.icon}
                  onClick={button.onClick}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }} // Left-align button content
                >
                  {button.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Footer Bottom Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: { xs: 4, sm: 8 },
          width: '100%',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <div>
          <Link
            component={RouterLink}
            to="/privacy"
            color="text.secondary"
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            Privacy Policy
          </Link>
          <Typography sx={{ display: 'inline', mx: 0.5, opacity: 0.5 }}>&nbsp;•&nbsp;</Typography>
          <Link
            component={RouterLink}
            to="/terms"
            color="text.secondary"
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            Terms of Service
          </Link>
          <Copyright />
        </div>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ justifyContent: 'left', color: 'text.secondary' }}
        >
          <IconButton
            color="inherit"
            size="small"
            onClick={() => window.open('https://www.linkedin.com/company/falconxcellence/')}
            aria-label="LinkedIn"
            sx={{ alignSelf: 'center' }}
          >
            <LinkedInIcon />
          </IconButton>
        </Stack>
      </Box>
    </Container>
  );
}
